<script lang="ts" setup>
	import {Avatar, AvatarFallback, AvatarImage} from '@/components/ui/avatar';

	import {
		Collapsible,
		CollapsibleContent,
		CollapsibleTrigger,
	} from '@/components/ui/collapsible';
	import {
		DropdownMenu,
		DropdownMenuContent,
		DropdownMenuGroup,
		DropdownMenuItem,
		DropdownMenuLabel,
		DropdownMenuSeparator,
		DropdownMenuTrigger,
	} from '@/components/ui/dropdown-menu';
	import {
		Sidebar,
		SidebarContent,
		SidebarFooter,
		SidebarGroup,
		SidebarGroupLabel,
		SidebarHeader,
		SidebarMenu,
		SidebarMenuButton,
		SidebarMenuItem,
		SidebarMenuSub,
		SidebarMenuSubButton,
		SidebarMenuSubItem,
		SidebarRail,
		useSidebar,
	} from '@/components/ui/sidebar';
	import {
		Bell,
		Calendar,
		ChartLine,
		ChevronRight,
		ChevronsUpDown,
		Cog,
		CopyCheck,
		CreditCard,
		GalleryVertical,
		GitCompare,
		Home,
		LayoutDashboard,
		LogOut,
		MessageSquare,
		MessagesSquare,
		MoveRight,
		Plus,
		Sparkles,
		User,
		Video,
	} from 'lucide-vue-next';
	import {useAuthentication} from '@/scripts/composables/use-authentication.ts';
	import {router} from '@inertiajs/core';
	import {MenuItem} from '@/scripts/types/helper.ts';
	import ProjectCreate from './partials/project-create.vue';
	import {useRoute} from '@/scripts/composables/use-route.ts';
	import {trans, transChoice} from 'laravel-vue-i18n';
	import {getRoleName} from '@/scripts/utils/users.ts';

	const {user, fullName, initials} = useAuthentication();
	const {
		setProjects,
		projects,
		activeProject,
		setActiveProject,
		clearActiveProject,
	} = useProjects();

	const $page = usePage();

	onMounted(() => {
		setProjects($page.props.projects);
	});

	// This is the menu
	const menu = computed<Record<string, MenuItem[]>>(() => ({
		[trans('ui.sections.overview')]: [
			{
				label: trans('ui.common.dashboard'),
				url: route('tenant.home'),
				icon: Home,
			},
			{
				label: transChoice('domains.tenant.process.title', 2),
				url: route('tenant.processes.index'),
				icon: MoveRight,
				visible: user.value?.can.process.view,
			},
			{
				label: transChoice('domains.tenant.activity.title', 2),
				url: route('tenant.work-items.index'),
				icon: GitCompare,
				visible: user.value?.can.workItem.view,
			},
			{
				label: trans('ui.common.analytics'),
				url: '#',
				icon: ChartLine,
			},
		],
		[trans('ui.sections.collaboration_communication')]: [
			{
				label: trans('ui.common.chat'),
				url: '#',
				icon: MessageSquare,
			},
			{
				label: trans('domains.tenant.message.project_feed'),
				url: route('tenant.feed-messages.index'),
				icon: MessagesSquare,
				visible: user.value?.can.message.view,
				badge: $page.props.messageCount,
			},
			{
				label: trans('ui.common.tasks'),
				url: '#',
				icon: CopyCheck,
			},
			{
				label: trans('ui.common.meetings'),
				url: '#',
				icon: Video,
			},
			{
				label: trans('ui.common.calendar'),
				url: '#',
				icon: Calendar,
			},
			{
				label: transChoice(
					'domains.tenant.client.title',
					activeProject.value ? 1 : 2
				),
				url: activeProject.value
					? route('tenant.clients.show', {
							client: activeProject.value.client.uuid,
						})
					: route('tenant.clients.index'),
				icon: User,
				visible: user.value?.can.client.view,
			},
		],
	}));

	const {isActive} = useRoute();

	const isDialogOpen = ref<boolean>(false);

	const {isAvailable} = useSidebar();

	const logOut = () => {
		router.post(route('tenant.logout'));
	};

	watch(
		isAvailable,
		() => {
			setProjects($page.props.projects ?? []);
		},
		{
			immediate: true,
		}
	);
</script>

<template>
	<Sidebar :collapsible="isAvailable ? 'icon' : 'offcanvas'">
		<SidebarHeader>
			<SidebarMenu>
				<SidebarMenuItem>
					<Dialog
						v-model:open="isDialogOpen"
						modal
					>
						<DropdownMenu v-if="projects && projects.length > 0">
							<DropdownMenuTrigger as-child>
								<SidebarMenuButton
									class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
									size="lg"
								>
									<div
										class="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg"
									>
										<component
											:is="
												activeProject?.name
													? GalleryVertical
													: LayoutDashboard
											"
											class="size-4"
										/>
									</div>
									<div
										class="grid flex-1 text-left text-sm leading-tight"
									>
										<span class="truncate font-semibold">{{
											activeProject?.name ??
											$t('domains.tenant.project.overall')
										}}</span>
										<span
											v-if="activeProject?.name"
											class="truncate text-xs"
										>
											{{ activeProject.client.name }}
										</span>
									</div>
									<ChevronsUpDown class="ml-auto" />
								</SidebarMenuButton>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								:side-offset="4"
								align="start"
								class="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
								side="bottom"
							>
								<DropdownMenuLabel
									class="text-muted-foreground text-xs"
								>
									{{
										$tChoice(
											'domains.tenant.project.title',
											2
										)
									}}
								</DropdownMenuLabel>
								<DropdownMenuItem
									class="gap-2 p-2"
									@click="clearActiveProject()"
								>
									<div
										class="flex size-6 items-center justify-center rounded-sm border"
									>
										<LayoutDashboard
											class="size-4 shrink-0"
										/>
									</div>
									{{ $t('domains.tenant.project.overall') }}
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									v-for="project in projects"
									:key="project.slug"
									class="gap-2 p-2"
									@click="setActiveProject(project.uuid)"
								>
									<div
										class="flex size-6 items-center justify-center rounded-sm border"
									>
										<component
											:is="GalleryVertical"
											class="size-4 shrink-0"
										/>
									</div>
									{{ project.name }}
									<!--									<DropdownMenuShortcut-->
									<!--										>⌘{{ index + 1 }}-->
									<!--									</DropdownMenuShortcut>-->
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									v-if="user?.can?.project?.create"
									class="p-2"
								>
									<DialogTrigger>
										<div class="flex items-center gap-2">
											<div
												class="bg-background flex size-6 items-center justify-center rounded-md border"
											>
												<Plus class="size-4" />
											</div>
											<div
												class="text-muted-foreground font-medium"
											>
												{{
													$t(
														'domains.tenant.project.create.title'
													)
												}}
											</div>
										</div>
									</DialogTrigger>
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>

						<SidebarMenuButton
							v-else-if="user?.can?.project?.create"
						>
							<DialogTrigger>
								<div class="flex items-center gap-2">
									<div
										class="bg-background flex size-6 items-center justify-center rounded-md border"
									>
										<Plus class="size-4" />
									</div>
									<div
										class="text-muted-foreground font-medium"
									>
										{{
											$t(
												'domains.tenant.project.create.title'
											)
										}}
									</div>
								</div>
							</DialogTrigger>
						</SidebarMenuButton>
						<div
							v-else
							class="bg-sidebar-accent text-sidebar-accent-foreground flex items-center gap-2"
						>
							<div
								class="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg"
							>
								<LayoutDashboard class="size-4" />
							</div>
							<div
								class="grid flex-1 text-left text-sm leading-tight"
							>
								<span class="truncate font-semibold"
									>{{ $t('domains.tenant.project.overall') }}
								</span>
							</div>
						</div>
						<ProjectCreate
							v-if="isAvailable && user?.can?.project?.create"
							v-model="isDialogOpen"
						/>
					</Dialog>
				</SidebarMenuItem>
			</SidebarMenu>
		</SidebarHeader>
		<SidebarContent>
			<SidebarGroup
				v-for="(group, name) in menu"
				:key="name"
			>
				<SidebarGroupLabel>{{ name }}</SidebarGroupLabel>
				<SidebarMenu>
					<template
						v-for="item in group"
						:key="item.label as string"
					>
						<Collapsible
							v-if="
								'items' in item &&
								(item.visible || item.visible === undefined)
							"
							as-child
							class="group/collapsible"
						>
							<SidebarMenuItem>
								<CollapsibleTrigger as-child>
									<SidebarMenuButton
										:tooltip="item.label as string"
									>
										<component :is="item.icon" />
										<span>{{ item.label as string }}</span>
										<ChevronRight
											class="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
										/>
									</SidebarMenuButton>
								</CollapsibleTrigger>
								<CollapsibleContent>
									<SidebarMenuSub>
										<template
											v-for="subItem in item.items"
											:key="subItem.label as string"
										>
											<SidebarMenuSubItem
												v-if="
													subItem.visible ||
													subItem.visible ===
														undefined
												"
											>
												<SidebarMenuSubButton as-child>
													<a :href="subItem.url">
														<span>{{
															subItem.label as string
														}}</span>
													</a>
												</SidebarMenuSubButton>
											</SidebarMenuSubItem>
										</template>
									</SidebarMenuSub>
								</CollapsibleContent>
							</SidebarMenuItem>
						</Collapsible>
						<SidebarMenuItem
							v-else-if="
								item.visible || item.visible === undefined
							"
						>
							<SidebarMenuButton
								:is-active="isActive(item.url)"
								:tooltip="item.label as string"
								as-child
							>
								<RouterLink
									:href="item.url"
									class="flex items-center"
								>
									<component
										:is="item.icon"
										class="size-4"
									/>
									<span>{{ item.label as string }}</span>

									<span
										v-if="item.badge"
										class="bg-primary text-primary-foreground ml-auto inline-flex size-5 items-center justify-center rounded-full text-center"
									>
										{{ item.badge }}
									</span>
								</RouterLink>
							</SidebarMenuButton>
						</SidebarMenuItem>
					</template>
				</SidebarMenu>
			</SidebarGroup>
		</SidebarContent>
		<SidebarFooter>
			<SidebarMenu>
				<SidebarMenuItem>
					<DropdownMenu>
						<DropdownMenuTrigger as-child>
							<SidebarMenuButton
								class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
								size="lg"
							>
								<Avatar class="h-8 w-8 rounded-lg">
									<AvatarImage
										:alt="fullName"
										:src="''"
									/>
									<AvatarFallback class="rounded-lg">
										{{ initials }}
									</AvatarFallback>
								</Avatar>
								<div
									class="grid flex-1 text-left text-sm leading-tight"
								>
									<span class="truncate font-semibold">{{
										fullName
									}}</span>
									<span class="truncate text-xs">{{
										getRoleName(user?.role)
									}}</span>
								</div>
								<ChevronsUpDown class="ml-auto size-4" />
							</SidebarMenuButton>
						</DropdownMenuTrigger>
						<DropdownMenuContent
							:side-offset="4"
							align="end"
							class="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
							side="bottom"
						>
							<DropdownMenuLabel class="p-0 font-normal">
								<div
									class="flex items-center gap-2 px-1 py-1.5 text-left text-sm"
								>
									<Avatar class="h-8 w-8 rounded-lg">
										<AvatarImage
											:alt="fullName"
											:src="''"
										/>
										<AvatarFallback class="rounded-lg">
											{{ initials }}
										</AvatarFallback>
									</Avatar>
									<div
										class="grid flex-1 text-left text-sm leading-tight"
									>
										<span class="truncate font-semibold">{{
											fullName
										}}</span>
										<span class="truncate text-xs">{{
											user?.role
												? $t(
														`domains.tenant.settings.members.roles.${user.role}`
													)
												: ''
										}}</span>
									</div>
								</div>
							</DropdownMenuLabel>
							<DropdownMenuSeparator />
							<DropdownMenuGroup>
								<DropdownMenuItem>
									<Sparkles />
									{{ $t('ui.common.upgrade_pro') }}
								</DropdownMenuItem>
							</DropdownMenuGroup>
							<DropdownMenuSeparator />
							<DropdownMenuGroup>
								<DropdownMenuItem as-child>
									<RouterLink
										:href="
											route(
												'tenant.settings.profile.index',
												{
													user: user.uuid,
												}
											)
										"
									>
										<Cog />
										{{ $t('ui.common.settings') }}
									</RouterLink>
								</DropdownMenuItem>
								<DropdownMenuItem>
									<CreditCard />
									{{ $t('ui.common.billing') }}
								</DropdownMenuItem>
								<DropdownMenuItem>
									<Bell />
									{{ $t('ui.common.notifications') }}
								</DropdownMenuItem>
							</DropdownMenuGroup>
							<DropdownMenuSeparator />
							<DropdownMenuItem @click="logOut()">
								<LogOut />
								{{ $t('auth.log_out') }}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</SidebarMenuItem>
			</SidebarMenu>
		</SidebarFooter>
		<SidebarRail />
	</Sidebar>
</template>
